import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DTypeForm, DTypeFormTriggerField } from '@grupo-constant/next-dtype/form';
import { NextLocator, TriggerEvent, TRIGGER_COLUMN_URI } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, firstValueFrom, Subject, takeUntil, throwError } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { FormBaseColumns, FormSummary, Task } from '../../modules/admin/tasks/tasks.types';
import { NextTaskTypeForm } from '../../core/task-type/task-type.types';
import { AuthService } from '../../core/auth/auth.service';
import { CompanyService } from '../../core/company/company.service';
import { FuseConfigService } from '../../../@fuse/services/config/config.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { DTypeService } from '../../core/dtype/dtype.service';
import { FormService } from '../../core/form/form.service';
import { LocalizeService } from '../../core/pipes/localize.service';
import { LocalizePipe } from '../../core/pipes/localize.pipe';
import { UserService } from '../../core/user/user.service';
import { FlushFormBody, Form } from '../../core/form/form.types';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-task-form-dialog',
    templateUrl: './task-form-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
        .uris-grid {
            grid-template-columns: 200px auto;

            @screen sm {
                grid-template-columns: 200px auto;
            }

            @screen md {
                grid-template-columns: 200px auto;
            }

            @screen lg {
                grid-template-columns: 200px auto;
            }
            @screen xl {
                grid-template-columns: 200px auto;
            }
            @screen 2xl {
                grid-template-columns: 200px auto;
            }
            @screen 3xl {
                grid-template-columns: 200px auto;
            }
        }

        `
    ],
    styleUrls: ['./task-form-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskFormDialogComponent implements OnInit, OnDestroy {

    dtypeForm: DTypeForm;
    dtypeData: any;
    hasChanges = false;
    viewForm = true;

    showBOPreview = true;
    previewDeviceUrl: SafeUrl;
    ionicMode: 'md' | 'ios' = 'md';
    themeMode: 'auto' | 'light' | 'dark';

    tableData: FormBaseColumns = {
        _serviceId: { label: null, value: null },
        _taskId: { label: null, value: null },
        _oid: { label: null, value: null },
        _formRelease: { label: null, value: null },
        _userId: { label: null, value: null },
        _lastUpdateUserId: { label: null, value: null }
    };

    tableDates: FormBaseColumns = {
        _creationDate: { label: null, value: null },
        _queueDate: { label: null, value: null },
        _receivedDate: { label: null, value: null },
        _lastUpdate: { label: null, value: null }
    };

    tableUris: FormBaseColumns = {
        _taskTypeUri: { label: null, value: null },
        _formUri: { label: null, value: null }
    };


    private _unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            form: FormSummary;
            taskTypeForm: NextTaskTypeForm;
            task: Task;
            locator: NextLocator;
            readonly: boolean;
        },
        private authService: AuthService,
        private companyService: CompanyService,
        public dialogRef: MatDialogRef<TaskFormDialogComponent>,
        private domSanitizer: DomSanitizer,
        private _fuseConfigService: FuseConfigService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService,
        private dtypeService: DTypeService,
        private dialogsService: DialogsService,
        private formService: FormService,
        private localizeService: LocalizeService,
        private localize: LocalizePipe,
        private userService: UserService
    ) { }

    async ngOnInit() {
        try {
            if (this.data.form.oid) {
                this.dtypeData = await firstValueFrom(this.dtypeService.retrieveDtypeDataItemByUriAndOid(this.data.form.formUri, this.data.form.oid));
            } else {
                this.dtypeData = {};
            }
            this.dtypeForm = await this.dtypeService.getDtypeFormByUri({ uri: this.data.form.formUri, locator: this.data.locator, bindableOptions: true }, this.dtypeData,
                { readonly: this.data.readonly, isUpdate: true });
            const form = this.dtypeForm.dType as Form;
            this.showBOPreview = !form.editWithPreview;
            if (!this.showBOPreview) {
                const config = await firstValueFrom(this._fuseConfigService.config$);
                this.themeMode = config?.scheme ? config.scheme : 'auto';
                this.generatePreviewUrl();
                window.addEventListener('message', (event) => {
                    if (environment.previewUrl.startsWith(event.origin)) {
                        if (event.data?.formSummary) {
                            this.closeDialog(event.data?.formSummary);
                        }
                    }
                });
                this._fuseConfigService.config$
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((conf) => {
                        if (conf?.scheme) {
                            if (this.themeMode !== conf.scheme) {
                                this.themeMode = conf.scheme;
                                this.generatePreviewUrl();
                            }
                        } else {
                            if (this.themeMode !== 'auto') {
                                this.themeMode = 'auto';
                                this.generatePreviewUrl();
                            }
                        }
                    });
            }
            if (this.data.form.oid) {
                this.fillTableData();
            }
            await this.fillTriggerFields([TriggerEvent.ON_OPEN_RESET]);
            if (this.data.readonly) {
                this.dialogsService.openInfoDialog({
                    message: this.transloco.translate('taskWorkingCantEditForm')
                });
            }
        } catch (error) {
            console.error(error);
            this.dialogsService.openErrorDialog({
                message: error?.error?.echo ? this.localize.transform(error.error.echo) : undefined,
            });
        }
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    fillTableData() {
        for (const key of Object.keys(this.dtypeData)) {
            switch (key) {
                case '_formUri':
                case '_taskTypeUri':
                    this.tableUris[key].label = this.localize.transform(this.dtypeForm.fields[key].label);
                    this.tableUris[key].value = this.dtypeData[key];
                    break;
                case '_serviceId':
                case '_taskId':
                case '_oid':
                case '_formRelease':
                case '_userId':
                case '_lastUpdateUserId':
                    this.tableData[key].label = this.localize.transform(this.dtypeForm.fields[key].label);
                    this.tableData[key].value = this.dtypeData[key];
                    break;
                case '_creationDate':
                case '_queueDate':
                case '_receivedDate':
                case '_lastUpdate':
                    this.tableDates[key].label = this.localize.transform(this.dtypeForm.fields[key].label);
                    this.tableDates[key].value = this.localizeService.transformDate(this.dtypeData[key], 'dd/MM/yyyy HH:mm');
                    break;

            }
        }
    }

    returnObjectKeys(object: any) {
        return Object.keys(object);
    }

    returnObjectValues(object: any) {
        return Object.values(object);
    }

    async save() {
        const confirm = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('saveFormModifiedConfirm'));
        const form = this.dtypeForm.control;
        form.markAllAsTouched();
        if (confirm) {
            if (form.valid) {
                await this.fillTriggerFields([
                    TriggerEvent.ON_CREATE,
                    TriggerEvent.ON_QUEUE,
                    TriggerEvent.ON_QUEUE_RESET,
                    TriggerEvent.ON_SENDING]);
                const body: FlushFormBody = {
                    uri: this.data.form.formUri,
                    data: this.getControlValue(),
                    serviceId: this.data.task.serviceId,
                    taskId: this.data.task.taskId
                };
                this.formService.flushFormData(body).pipe(
                    catchError((error) => {
                        console.error(error);
                        this.dialogsService.openErrorDialog({
                            message: error?.echo.length > 0 ? error.echo : undefined
                        });
                        return throwError(() => error);
                    })
                ).subscribe((data) => {
                    this.closeDialog(data);
                });
            } else {
                this.dialogsService.openErrorDialog({
                    message: this.transloco.translate('errorsInForm')
                });
            }
        }
    }

    async closeDialog(result?: any) {
        if (this.hasChanges) {
            const confirm = await this.confirm(null, null, this.transloco.translate('exitWithoutSaving'));
            if (confirm) {
                this.dialogRef.close(result);
            }
        } else {
            this.dialogRef.close(result);
        }
    }

    changeMode() {
        this.generatePreviewUrl();
    }

    reloadIframe() {
        const iframe = document.getElementById('deviceIframe') as HTMLIFrameElement;
        iframe.contentWindow.postMessage('reload', environment.previewUrl);
    }

    private getControlValue() {
        const value = this.dtypeForm.control.getRawValue();
        for (const field of Object.values(this.dtypeForm.fields)) {
            if (field.name !== 'ac' && field.hidden && field.type !== TRIGGER_COLUMN_URI) {
                delete value[field.name];
            }
        }
        return value;
    }

    private async fillTriggerFields(event: TriggerEvent[]) {
        for (const field of Object.values(this.dtypeForm.fields)
            .filter(f => f.type === TRIGGER_COLUMN_URI)) {
            const needsToBeFilled = this.formService.checkTriggerField({
                value: this.dtypeForm.control.get(field.name).value,
                triggerField: (field as DTypeFormTriggerField),
                event
            });

            if (needsToBeFilled) {
                const value = await this.formService.getTriggerFieldValue((field as DTypeFormTriggerField), this.data.taskTypeForm, this.data.task);
                if (value) {
                    this.dtypeForm.control.get(field.name).setValue(value);
                }
            }
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

    private async generatePreviewUrl() {
        if (!this.authService.accessTokenIsValid()) {
            try {
                await firstValueFrom(this.authService.doRefreshToken());
            } catch (error) {
                console.error(error);
            }
        }
        const accessToken = this.authService.accessToken;
        const user = await firstValueFrom(this.userService.get());
        const company = await firstValueFrom(this.companyService.get());
        let url = `${environment.previewUrl}?uri=${this.dtypeForm.dType.uri}&edit=true&readonly=${this.data.readonly}`
            + `&serviceId=${this.data.task.serviceId}&taskId=${this.data.task.taskId}&userId=${user.userId}&companyId=${company.companyId}`
            + `&ionic:mode=${this.ionicMode}&accessToken=${accessToken}`;
        if (this.data.form.oid) {
            url += `&oid=${this.data.form.oid}`;
        }
        if (this.themeMode !== 'auto') {
            url += `&theme=${this.themeMode}`;
        }
        this.previewDeviceUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
    }
}
